import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * Reusable Popover component
 * - Keyboard accessible (Escape to close)
 * - Closes on outside click
 * - Supports dark mode
 * - Accepts custom trigger and content
 */
const Popover = ({
  trigger,
  children,
  position = 'bottom-left',
  className = '',
  contentClassName = '',
  portal = false,
  onOpen,
  onClose,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewportShift, setViewportShift] = useState(0);
  const [portalPosition, setPortalPosition] = useState({ top: 0, left: 0, visible: false });
  const popoverRef = useRef(null);
  const triggerRef = useRef(null);
  const viewportShiftRef = useRef(0);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    onOpen?.();
  }, [onOpen]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  const handleToggle = useCallback(() => {
    if (isOpen) {
      handleClose();
    } else {
      handleOpen();
    }
  }, [isOpen, handleOpen, handleClose]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, handleClose]);

  // Keep edge-anchored popovers inside the viewport. This matters when a
  // header is rendered inside a narrow sidebar rather than across the page.
  useLayoutEffect(() => {
    if (!isOpen || portal || position.includes('center')) return undefined;

    const updateViewportPosition = () => {
      const element = popoverRef.current;
      if (!element) return;
      const rect = element.getBoundingClientRect();
      const currentShift = viewportShiftRef.current;
      const baseLeft = rect.left - currentShift;
      const baseRight = rect.right - currentShift;
      const margin = 8;
      let nextShift = 0;

      if (baseLeft < margin) nextShift = margin - baseLeft;
      else if (baseRight > window.innerWidth - margin) {
        nextShift = (window.innerWidth - margin) - baseRight;
      }

      viewportShiftRef.current = nextShift;
      setViewportShift(nextShift);
    };

    updateViewportPosition();
    window.addEventListener('resize', updateViewportPosition);
    return () => window.removeEventListener('resize', updateViewportPosition);
  }, [isOpen, portal, position]);

  useLayoutEffect(() => {
    if (!isOpen || !portal) return undefined;

    const updatePortalPosition = () => {
      const triggerElement = triggerRef.current;
      const popoverElement = popoverRef.current;
      if (!triggerElement || !popoverElement) return;

      const triggerRect = triggerElement.getBoundingClientRect();
      const width = popoverElement.offsetWidth;
      const height = popoverElement.offsetHeight;
      const margin = 8;
      let left = position.endsWith('right')
        ? triggerRect.right - width
        : position.endsWith('center')
          ? triggerRect.left + ((triggerRect.width - width) / 2)
          : triggerRect.left;
      let top = position.startsWith('top')
        ? triggerRect.top - height - margin
        : triggerRect.bottom + margin;

      left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));
      top = Math.max(margin, Math.min(top, window.innerHeight - height - margin));
      setPortalPosition({ top, left, visible: true });
    };

    updatePortalPosition();
    window.addEventListener('resize', updatePortalPosition);
    window.addEventListener('scroll', updatePortalPosition, true);
    return () => {
      window.removeEventListener('resize', updatePortalPosition);
      window.removeEventListener('scroll', updatePortalPosition, true);
    };
  }, [isOpen, portal, position]);

  useEffect(() => {
    if (!isOpen) {
      viewportShiftRef.current = 0;
      setViewportShift(0);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        handleClose();
        triggerRef.current?.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, handleClose]);

  // Position classes
  const positionClasses = {
    'bottom-left': 'top-full left-0 mt-2',
    'bottom-right': 'top-full right-0 mt-2',
    'bottom-center': 'top-full left-1/2 -translate-x-1/2 mt-2',
    'top-left': 'bottom-full left-0 mb-2',
    'top-right': 'bottom-full right-0 mb-2',
    'top-center': 'bottom-full left-1/2 -translate-x-1/2 mb-2',
  };

  const popoverContent = isOpen ? (
    <div
      ref={popoverRef}
      className={`
        ${portal ? 'fixed' : 'absolute'} z-[90] max-w-[calc(100vw-1rem)]
        bg-white dark:bg-gray-800
        border border-gray-200 dark:border-gray-700
        rounded-xl shadow-lg
        animate-fade-in
        transition-[background-color,border-color] duration-250 ease-out
        ${portal ? '' : (positionClasses[position] || positionClasses['bottom-left'])}
        ${contentClassName}
      `}
      role="menu"
      aria-orientation="vertical"
      style={portal
        ? {
            top: portalPosition.top,
            left: portalPosition.left,
            visibility: portalPosition.visible ? 'visible' : 'hidden',
          }
        : (viewportShift ? { transform: `translateX(${viewportShift}px)` } : undefined)}
    >
      {typeof children === 'function' ? children({ close: handleClose }) : children}
    </div>
  ) : null;

  return (
    <div className={`relative inline-block ${className}`}>
      {/* Trigger */}
      <div
        ref={triggerRef}
        onClick={handleToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        }}
        role="button"
        tabIndex={0}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        {trigger}
      </div>

      {/* Portaled content escapes sidebar overflow and stacking contexts. */}
      {portal && popoverContent ? createPortal(popoverContent, document.body) : popoverContent}
    </div>
  );
};

/**
 * Popover Menu Item - consistent styling for menu items
 */
export const PopoverMenuItem = ({
  children,
  onClick,
  icon,
  danger = false,
  className = '',
}) => {
  return (
    <button
      onClick={onClick}
      className={`
        w-full flex items-center gap-3 px-4 py-2.5 text-left
        transition-colors duration-250 ease-out
        ${danger
          ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
        }
        ${className}
      `}
      role="menuitem"
    >
      {icon && <span className="w-5 h-5 flex-shrink-0">{icon}</span>}
      <span className="font-medium">{children}</span>
    </button>
  );
};

/**
 * Popover Divider
 */
export const PopoverDivider = () => (
  <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
);

/**
 * Popover Footer - for footer links like Terms, Privacy
 */
export const PopoverFooter = ({ children, className = '' }) => (
  <div
    className={`
      px-4 py-3 bg-gray-50 dark:bg-gray-900/50
      border-t border-gray-200 dark:border-gray-700
      rounded-b-xl
      ${className}
    `}
  >
    {children}
  </div>
);

export default Popover;
